import React, { useState, useMemo, useEffect } from 'react';
import { createNavigationItems } from './sideNavigationList';
import NoSSR from 'Components/NoSSR';
import Header from './NavigationItem';
import Category from './NavigationGroup';
import SubItem from './NavigationItem';
import TeamItem from './NavigationItem';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { isWebviewMode } from 'Utils/webviewMode';

const SideNavigation = ({ data, isSideNavOpen, setIsSideNavOpen }) => {
  const history = useHistory();
  const location = useLocation();

  const [openLeagues, setOpenLeagues] = useState([]);
  const isWebView = isWebviewMode(history?.location?.search);

  const navigationItems = useMemo(() => {
    if (!data) return [];
    return createNavigationItems(data);
  }, [data]);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2 && pathParts[0] === 'lig-merkezi') {
      const currentSlug = pathParts[1];
      const foundLeague = navigationItems.find(
        item => item.isSubItem && item.hasTeams && item.slug && item.slug.split('/').pop() === currentSlug
      );
      if (foundLeague) {
        setOpenLeagues([foundLeague.name]);
      }
    }
  }, [location.pathname, navigationItems]);

  const toggleTeams = leagueName => {
    setOpenLeagues(prevOpenLeagues => {
      if (prevOpenLeagues.includes(leagueName)) {
        return prevOpenLeagues.filter(name => name !== leagueName);
      } else {
        return [...prevOpenLeagues, leagueName];
      }
    });
  };

  const handleTeamSelect = leagueName => {
    setOpenLeagues(prevOpenLeagues => {
      if (!prevOpenLeagues.includes(leagueName)) {
        return [...prevOpenLeagues, leagueName];
      }
      return prevOpenLeagues;
    });
  };

  if (navigationItems.length === 0) {
    return <div>Yükleniyor...</div>;
  }

  const enhancedList = addOpenStateToList(navigationItems, openLeagues);
  const organizedList = organizeListWithTeams(enhancedList, openLeagues);

  return (
    <NoSSR>
      <>
        <div className={ `league-side-navigation${isSideNavOpen ? ' open' : ''} ${isWebView ? 'webview' : 'desktopview'}` }>
          { organizedList.map((item, index) => renderNavigationItem(item, index, toggleTeams, handleTeamSelect, setIsSideNavOpen)) }
        </div>
        { isSideNavOpen && <div className="side-nav-overlay" onClick={ () => setIsSideNavOpen(false) } /> }
      </>
    </NoSSR>
  );
};

const addOpenStateToList = (list, openLeagues) => {
  return list.map(item => ({
    ...item,
    isOpen: openLeagues.includes(item.name)
  }));
};

const organizeListWithTeams = (list, openLeagues) => {
  const result = [];
  let selectedTeamLeague = null;
  const currentPath = window.location.pathname;
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    if (item.isTeam && currentPath === item.link && item.leagueName) {
      selectedTeamLeague = item.leagueName;
      break;
    }
  }
  let openLeaguesWithSelected = openLeagues;
  if (selectedTeamLeague && !openLeagues.includes(selectedTeamLeague)) {
    openLeaguesWithSelected = [...openLeagues, selectedTeamLeague];
  }
  let skipTeamsFlag = true;
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    if (!item.isTeam) {
      result.push(item);
      if (item.isSubItem && openLeaguesWithSelected.includes(item.name)) {
        skipTeamsFlag = false;
      } else if (item.isSubItem) {
        skipTeamsFlag = true;
      }
    } else if (!skipTeamsFlag) {
      result.push(item);
    }
  }
  return result;
};

const renderNavigationItem = (item, index, toggleTeams, handleTeamSelect, setIsSideNavOpen) => {
  const history = useHistory();
  const { innerWidth } = window;
  const isMobile = innerWidth <= 640;

  if (item?.isDivider) {
    return <div key={ `divider-${index}` } className="divider" style={ { margin: '12px 0', width: '100%' } } />;
  }
  if (item.isHeader) {
    return <Header
      key={ `header-${index}` }
      item={ item }
      isHeader={ true }
      onClick={ isMobile ? () => setIsSideNavOpen(false) : undefined }
    />;
  }
  if (item.isCategory) {
    if (item.name === 'Canlı Yayın Rehberi' || item.name === 'Canlı Yayın Rehberi') {
      let baseLink = '/lig-merkezi/canli-yayin-rehberi';
      const search = history?.location?.search;
      
      if (search && (search.includes('rendertype') || search.includes('channel'))) {
        baseLink += search;
      }
      
      return (
        <Header
          key={ `stream-guide-${index}` }
          item={ {
            ...item,
            link: baseLink,
            selectable: true
          } }
          onClick={ isMobile ? () => setIsSideNavOpen(false) : undefined }
        />
      );
    }
    return <Category
      key={ `category-${index}` }
      group={ item }
      isCategory={ true }
      onClick={ isMobile ? () => setIsSideNavOpen(false) : undefined }
    />;
  }
  if (item.isSubItem) {
    return (
      <SubItem
        key={ `subitem-${index}` }
        item={ item }
        isSubItem={ true }
        hasTeams={ item?.hasTeams }
        onToggleTeams={ toggleTeams }
        onClick={ isMobile ? () => setIsSideNavOpen(false) : undefined }
      />
    );
  }
  if (item.isTeam) {
    return <TeamItem
      key={ `team-${index}` }
      item={ { ...item, leagueName: item.leagueName } }
      isTeam={ true }
      onTeamSelect={ handleTeamSelect }
      onClick={ isMobile ? () => setIsSideNavOpen(false) : undefined }
    />;
  }
  if (item.isStreamGuide) {
    return (
      <Header
        key={ `stream-guide-${index}` }
        item={ item }
        onClick={ isMobile ? () => setIsSideNavOpen(false) : undefined }
      />
    );
  }
  return null;
};

SideNavigation.propTypes = {
  data: PropTypes.object.isRequired,
  isSideNavOpen: PropTypes.bool,
  setIsSideNavOpen: PropTypes.func
};

export default SideNavigation;
